/**
 * tcAssistantStream — client-side reader for the Phase 13 SSE endpoint.
 *
 * Contract with backend (see server.py `staffing_assistant_chat`):
 *   POST /api/staffing/assistant/chat  (JSON body)
 *   Response: text/event-stream
 *   Events:
 *     data: {"type":"chunk","content":"tokens"}
 *     data: {"type":"done","total_chars":N,"partial":bool?}
 *     data: {"type":"error","message":"..."}
 *
 * On any transport failure (network, 4xx, 5xx, empty body) `onError`
 * fires exactly once. The caller then falls back to canned intents.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * @param {Object}   payload    request body (see AssistantChatRequest schema)
 * @param {(t:string)=>void} onChunk called per token
 * @param {(meta:{total_chars?:number,partial?:boolean})=>void} onDone
 * @param {(err:Error)=>void} onError single-fire failure hook (used for fallback)
 * @param {AbortSignal} signal   cancel the stream mid-flight
 */
export async function streamAssistantResponse(
  payload,
  onChunk,
  onDone,
  onError,
  signal,
) {
  let response;
  try {
    response = await fetch(`${BACKEND}/api/staffing/assistant/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify(payload),
      signal,
    });
  } catch (e) {
    if (signal?.aborted) return;
    onError(e instanceof Error ? e : new Error(String(e)));
    return;
  }

  if (!response.ok) {
    onError(new Error(`assistant_http_${response.status}`));
    return;
  }
  if (!response.body) {
    onError(new Error('assistant_no_body'));
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let seenAnyChunk = false;
  let errored = false;

  try {
    // Standard SSE parsing — each event ends with \n\n and each line inside
    // starts with `data: `. We don't need id / retry / event-type semantics.
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);

        // Each event may contain multiple `data:` lines — concat with \n.
        const dataLines = rawEvent
          .split('\n')
          .filter((l) => l.startsWith('data:'))
          .map((l) => l.slice(5).trimStart());
        if (dataLines.length === 0) continue;

        const dataStr = dataLines.join('\n');
        let payloadEvent;
        try {
          payloadEvent = JSON.parse(dataStr);
        } catch {
          continue;
        }

        if (payloadEvent.type === 'chunk' && typeof payloadEvent.content === 'string') {
          seenAnyChunk = true;
          onChunk(payloadEvent.content);
        } else if (payloadEvent.type === 'done') {
          onDone({
            total_chars: payloadEvent.total_chars,
            partial: !!payloadEvent.partial,
          });
          return;
        } else if (payloadEvent.type === 'error') {
          errored = true;
          onError(new Error(payloadEvent.message || 'assistant_error'));
          return;
        }
      }
    }

    // Stream ended without an explicit 'done' — treat as done if we got any
    // content, else fire error so the caller can fall back.
    if (seenAnyChunk && !errored) {
      onDone({ partial: true });
    } else if (!errored) {
      onError(new Error('assistant_empty_stream'));
    }
  } catch (e) {
    if (signal?.aborted) return;
    if (!errored) onError(e instanceof Error ? e : new Error(String(e)));
  }
}
